import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { FileCheck, Save } from "lucide-react";

export default function LicenseNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const [form, setForm] = useState({
    business_name: "", owner_name: user?.name || "",
    owner_email: user?.email || "", owner_phone: user?.phone || "",
    owner_aadhaar: "",
    business_type: "retail_shop",
    address: "", ward: "Ward-1", zone: "North",
    number_of_employees: 1, area_sqft: 300, capital_investment: 100000,
    municipal_body: "Ludhiana Municipal Corporation",
  });
  const [config, setConfig] = useState({ business_types: [] });
  const [wards, setWards] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/licenses/config").then((r) => setConfig(r.data));
    api.get("/master/wards").then((r) => setWards(r.data.items || []));
    api.get("/master/municipal-bodies").then((r) => setBodies(r.data.items || []));
  }, []);

  useEffect(() => {
    api.get("/licenses/calculator", { params: {
      business_type: form.business_type,
      area_sqft: Number(form.area_sqft) || 0,
      capital_investment: Number(form.capital_investment) || 0,
    }}).then((r) => setCalc(r.data));
  }, [form.business_type, form.area_sqft, form.capital_investment]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form,
        owner_aadhaar: form.owner_aadhaar ? `XXXX-XXXX-${form.owner_aadhaar}` : "",
        number_of_employees: Number(form.number_of_employees),
        area_sqft: Number(form.area_sqft),
        capital_investment: Number(form.capital_investment),
      };
      const r = await api.post("/licenses", payload);
      toast.success("Trade License application submitted", { description: r.data.license_code });
      nav(isCitizen ? `/portal/licenses/${r.data.id}` : `/licenses/${r.data.id}`);
    } catch (err) {
      toast.error("Submission failed", { description: err.response?.data?.detail || "Try again" });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="license-new-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Apply</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <FileCheck className="w-6 h-6 text-[#0D7650]" /> Trade License Application
        </h1>
      </div>

      <Card>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label>Business Name *</Label>
            <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} required data-testid="input-business-name" />
          </div>
          <div>
            <Label>Owner Name *</Label>
            <Input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} required data-testid="input-owner-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} />
          </div>
          <div>
            <Label>ID (last 4 digits)</Label>
            <Input maxLength={4} inputMode="numeric" value={form.owner_aadhaar} onChange={(e) => set("owner_aadhaar", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="1234" data-testid="license-input-owner-id-last4" />
          </div>
          <div>
            <Label>Business Type *</Label>
            <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
              <SelectTrigger data-testid="select-business-type"><SelectValue /></SelectTrigger>
              <SelectContent>{(config.business_types || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Business Address *</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} required data-testid="input-address" />
          </div>
          <div>
            <Label>Ward *</Label>
            <Select value={form.ward} onValueChange={(v) => set("ward", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={form.zone} onValueChange={(v) => set("zone", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["North", "South", "East", "West", "Central"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Employees</Label>
            <Input type="number" value={form.number_of_employees} onChange={(e) => set("number_of_employees", e.target.value)} />
          </div>
          <div>
            <Label>Area (sqft) *</Label>
            <Input type="number" value={form.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} data-testid="input-area" />
          </div>
          <div>
            <Label>Capital Investment (₹) *</Label>
            <Input type="number" value={form.capital_investment} onChange={(e) => set("capital_investment", e.target.value)} data-testid="input-capital" />
          </div>
          <div>
            <Label>Municipal Body</Label>
            <Select value={form.municipal_body} onValueChange={(v) => set("municipal_body", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {calc && (
        <Card className="bg-[#0F2942] text-white" data-testid="license-fee-card">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70">Estimated Annual Fee</div>
              <div className="text-4xl font-heading font-bold mt-1">{formatINR(calc.fee_amount)}</div>
            </div>
            <div className="text-sm opacity-80">Includes capital slab multiplier + per-sqft rate.</div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => nav(isCitizen ? "/portal" : "/licenses")}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-submit-license">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Submitting..." : "Submit Application"}
        </Button>
      </div>
    </form>
  );
}
